import React, { useCallback, useEffect, useState, startTransition } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAppState } from 'src/state-management/state-provider';
import {
  clearAuthTokens,
  addTokenRefreshListener,
  removeTokenRefreshListener,
} from 'src/clientService';
import clientService from 'src/clientService';
import FullPageLoader from 'src/view/components/FullPageLoader';
import FullscreenWrapper from 'src/view/components/FullscreenWrapper';
import AuthPage from 'src/view/AuthPage';
import PasswordResetPage from 'src/view/PasswordReset';
import ToolHub from 'src/view/ToolHub';
import AuthenticatedRoutes from './AuthenticatedRoutes';
import { Navigate } from 'react-router-dom';

const AppRouter: React.FC = () => {
  const { dispatch, state } = useAppState();
  const [loading, setLoading] = useState(true);

  /**
   * Fetch fresh user data from the server and update global state.
   * This is called on initial load and after every token refresh so
   * that role / status changes made by an admin are reflected promptly.
   */
  const fetchAndSetUser = useCallback(
    async (isMounted: { current: boolean }) => {
      try {
        const response = await clientService.api.getUserApiV1AuthUserGet();
        if (isMounted.current) {
          startTransition(() => {
            dispatch({
              type: 'SET_USER_DETAILS',
              payload: {
                ...response.data,
                role: response.data.role || [],
                status: (response.data.status as any) || 'Active',
                is_email_verified: response.data.is_email_verified || false,
              },
            });
          });
        }
      } catch {
        clearAuthTokens();
        if (isMounted.current) {
          startTransition(() => {
            dispatch({ type: 'CLEAR_USER_DETAILS' });
          });
        }
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const isMounted = { current: true };

    const verifyAuth = async () => {
      await fetchAndSetUser(isMounted);
      if (isMounted.current) {
        startTransition(() => {
          setLoading(false);
        });
      }
    };

    verifyAuth();

    // Re-fetch user after every silent token refresh so stale
    // role/status claims in the JWT do not drive UI decisions.
    const onTokenRefreshed = () => {
      fetchAndSetUser(isMounted);
    };
    addTokenRefreshListener(onTokenRefreshed);

    return () => {
      isMounted.current = false;
      removeTokenRefreshListener(onTokenRefreshed);
    };
  }, [dispatch, fetchAndSetUser]);

  if (loading) {
    return <FullscreenWrapper><FullPageLoader message="Verifying authentication..." title="Hang in there..." /></FullscreenWrapper>;
  }

  // If not authenticated after loading, redirect to AuthPage
  if (!state.user) {
    return (
      <Routes>
        <Route path="/" element={<ToolHub />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/verify-email-post-signup" element={<AuthPage />} />
        <Route path="/reset-password" element={<PasswordResetPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Do NOT wrap AuthenticatedRoutes in <Routes>
  return <AuthenticatedRoutes />;
};

export default AppRouter;
